import { supabase } from './supabase';
import { Database } from './database.types';

type Organization = Database['public']['Tables']['organizations']['Row'];
type OrganizationInsert = Database['public']['Tables']['organizations']['Insert'];
type OrganizationUpdate = Database['public']['Tables']['organizations']['Update'];

export interface CreateOrganizationParams {
  name: string;
  description?: string;
  createdBy: string;
}

export interface OrganizationInfo extends Organization {
  memberCount?: number;
  ownerName?: string;
}

export class OrganizationService {
  /**
   * Create a new organization
   */
  static async createOrganization(params: CreateOrganizationParams): Promise<Organization> {
    try {
      const { name, description, createdBy } = params;

      // Validate parameters
      if (!name || !createdBy) {
        throw new Error('Organization name and creator ID are required');
      }

      // Create organization
      const organizationData: OrganizationInsert = {
        name: name.trim(),
        description: description?.trim() || null,
        created_by: createdBy
      };

      const { data: organization, error } = await supabase
        .from('organizations')
        .insert(organizationData)
        .select()
        .single();

      if (error) {
        console.error('Error creating organization:', error);
        throw new Error(`Failed to create organization: ${error.message}`);
      }

      console.log('✅ Organization created successfully:', {
        id: organization.id,
        name: organization.name,
        createdBy: organization.created_by
      });

      return organization;
    } catch (error) {
      console.error('OrganizationService.createOrganization error:', error);
      throw error;
    }
  }

  /**
   * Get organization by ID
   */
  static async getOrganization(organizationId: string): Promise<OrganizationInfo | null> {
    try {
      const { data: organization, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', organizationId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Not found
        console.error('Error fetching organization:', error);
        throw new Error(`Failed to fetch organization: ${error.message}`);
      }

      // Get member count
      const { count: memberCount, error: countError } = await supabase
        .from('users')
        .select('id', { count: 'exact' })
        .eq('organization_id', organizationId);

      if (countError) {
        console.warn('Error fetching member count:', countError);
      }

      // Get owner name
      const { data: owner, error: ownerError } = await supabase
        .from('users')
        .select('name')
        .eq('id', organization.created_by)
        .single();

      if (ownerError) {
        console.warn('Error fetching owner name:', ownerError);
      }

      const organizationInfo: OrganizationInfo = {
        ...organization,
        memberCount: memberCount || 0,
        ownerName: owner?.name || 'Unknown'
      };

      return organizationInfo;
    } catch (error) {
      console.error('OrganizationService.getOrganization error:', error);
      return null;
    }
  }

  /**
   * Get organization for a specific user
   */
  static async getUserOrganization(userId: string): Promise<OrganizationInfo | null> {
    try {
      // Get user's organization_id
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', userId)
        .single();

      if (userError || !userData?.organization_id) {
        return null;
      }

      return await this.getOrganization(userData.organization_id);
    } catch (error) {
      console.error('OrganizationService.getUserOrganization error:', error);
      return null;
    }
  }

  /**
   * Update user's organization and role
   */
  static async updateUserOrganization(userId: string, organizationId: string, teamRole: 'admin' | 'editor' | 'viewer'): Promise<void> {
    try {
      const { error } = await supabase
        .from('users')
        .update({
          organization_id: organizationId,
          team_role: teamRole,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        console.error('Error updating user organization:', error);
        throw new Error(`Failed to update user organization: ${error.message}`);
      }

      console.log('✅ User organization updated:', {
        userId,
        organizationId,
        teamRole
      });
    } catch (error) {
      console.error('OrganizationService.updateUserOrganization error:', error);
      throw error;
    }
  }

  /**
   * Create organization for first-time team inviter
   */
  static async createFirstTimeOrganization(userId: string): Promise<Organization> {
    try {
      // Get user data to generate organization name
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('name, email')
        .eq('id', userId)
        .single();

      if (userError || !userData) {
        throw new Error('User not found');
      }

      // Check if user already has an organization
      const { data: existingUser, error: existingError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', userId)
        .single();

      if (existingError) {
        throw new Error('Failed to check existing organization');
      }

      if (existingUser.organization_id) {
        // User already has an organization, fetch and return it
        const organization = await this.getOrganization(existingUser.organization_id);
        if (organization) {
          return organization;
        }
      }

      // Generate organization name
      const organizationName = this.generateOrganizationName(userData.name, userData.email);

      // Create organization
      const organization = await this.createOrganization({
        name: organizationName,
        description: `Team organization for ${userData.name}`,
        createdBy: userId
      });

      // Update user as admin of the organization
      await this.updateUserOrganization(userId, organization.id, 'admin');

      console.log('✅ First-time organization created and user updated as admin');

      return organization;
    } catch (error) {
      console.error('OrganizationService.createFirstTimeOrganization error:', error);
      throw error;
    }
  }

  /**
   * Update organization details
   */
  static async updateOrganization(organizationId: string, updates: Partial<OrganizationUpdate>): Promise<Organization> {
    try {
      const { data: organization, error } = await supabase
        .from('organizations')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', organizationId)
        .select()
        .single();

      if (error) {
        console.error('Error updating organization:', error);
        throw new Error(`Failed to update organization: ${error.message}`);
      }

      return organization;
    } catch (error) {
      console.error('OrganizationService.updateOrganization error:', error);
      throw error;
    }
  }

  /**
   * Delete organization (only by owner)
   */
  static async deleteOrganization(organizationId: string, userId: string): Promise<void> {
    try {
      // Verify user is the owner
      const { data: organization, error: orgError } = await supabase
        .from('organizations')
        .select('created_by')
        .eq('id', organizationId)
        .single();

      if (orgError) {
        throw new Error('Organization not found');
      }

      if (organization.created_by !== userId) {
        throw new Error('Only organization owner can delete the organization');
      }

      // Remove organization_id from all users
      await supabase
        .from('users')
        .update({
          organization_id: null,
          team_role: null
        })
        .eq('organization_id', organizationId);

      // Delete organization
      const { error } = await supabase
        .from('organizations')
        .delete()
        .eq('id', organizationId);

      if (error) {
        console.error('Error deleting organization:', error);
        throw new Error(`Failed to delete organization: ${error.message}`);
      }

      console.log('✅ Organization deleted successfully');
    } catch (error) {
      console.error('OrganizationService.deleteOrganization error:', error);
      throw error;
    }
  }

  /**
   * Generate appropriate organization name based on user data
   */
  private static generateOrganizationName(userName: string, userEmail: string): string {
    // Try to extract company from email domain (exclude common email providers)
    const emailDomain = userEmail.split('@')[1]?.toLowerCase();
    const commonDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'aol.com'];
    
    if (emailDomain && !commonDomains.includes(emailDomain)) {
      // Use domain name as organization (e.g., "company.com" -> "Company")
      const companyName = emailDomain.split('.')[0];
      return companyName.charAt(0).toUpperCase() + companyName.slice(1);
    }

    // Fallback to user's name + "'s Team"
    return `${userName}'s Team`;
  }

  /**
   * Check if user can manage organization (is owner or admin)
   */
  static async canManageOrganization(userId: string, organizationId: string): Promise<boolean> {
    try {
      const { data: userData, error } = await supabase
        .from('users')
        .select('organization_id, team_role')
        .eq('id', userId)
        .single();

      if (error || !userData) {
        return false;
      }

      return userData.organization_id === organizationId && userData.team_role === 'admin';
    } catch (error) {
      console.error('Error checking organization management permissions:', error);
      return false;
    }
  }
}

export default OrganizationService;